import type { Beneficiary, Party, SuccessionAllocationMode } from '@/api/generated/model';
import type { AllocationSegment } from '@/components/planning/StackedAllocationBar';
import { StackedAllocationBar } from '@/components/planning/StackedAllocationBar';
import { SuccessionEstateAllocationEditor } from '@/components/planning/succession/SuccessionEstateAllocationEditor';
import { SuccessionModeToggle } from '@/components/planning/succession/SuccessionModeToggle';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { RollingNumber } from '@/components/ui/rolling-number';
import { ShimmerText } from '@/components/ui/shimmer-text';
import { useLanguage } from '@/i18n/language-hooks';
import { cn, formatCurrency } from '@/lib/utils';
import type { ReactNode } from 'react';

const SHIMMER_PARTY_NAMES = ['Alice', 'Bob'] as const;

interface SuccessionAllocationSummaryProps {
  title?: string;
  estateTotal: number;
  currency: string;
  beneficiaries: Beneficiary[];
  parties: Party[];
  percents: number[];
  onPercentsChange?: (percents: number[]) => void;
  segments?: AllocationSegment[];
  selectedBeneficiaryId: string | null;
  onSelectBeneficiary: (id: string | null) => void;
  onAddFromParty: (partyId: string) => void;
  onRemoveBeneficiary: (beneficiaryId: string) => void;
  allocationMode: SuccessionAllocationMode;
  onAllocationModeChange: (mode: SuccessionAllocationMode) => void;
  drilldownLabel?: string;
  drilldownContent?: ReactNode;
  /** When false, show waterfall segments only (by-account overview). */
  interactive?: boolean;
  isLoading?: boolean;
  isEstateLoading?: boolean;
}

export function SuccessionAllocationSummary({
  title,
  estateTotal,
  currency,
  beneficiaries,
  parties,
  percents,
  onPercentsChange,
  segments,
  selectedBeneficiaryId,
  onSelectBeneficiary,
  onAddFromParty,
  onRemoveBeneficiary,
  allocationMode,
  onAllocationModeChange,
  drilldownLabel,
  drilldownContent,
  interactive = true,
  isLoading = false,
  isEstateLoading = false,
}: SuccessionAllocationSummaryProps) {
  const { t } = useLanguage();
  const showEditor = interactive && onPercentsChange && !isLoading;
  const availableParties = parties.filter(
    (party) => !beneficiaries.some((beneficiary) => beneficiary.partyId === party.id)
  );
  const removeLabel = t('common.delete');
  const showShimmerChips = isLoading && beneficiaries.length === 0 && availableParties.length === 0;

  return (
    <Card>
      <CardHeader className='flex flex-row items-baseline justify-between gap-3'>
        <CardTitle className='text-base'>
          {title ?? t('planning.succession.simpleAllocation')}
        </CardTitle>
        <RollingNumber
          value={isEstateLoading ? 0 : estateTotal}
          format={(n) => formatCurrency(n, currency)}
          className='text-muted-foreground text-sm tabular-nums'
        />
      </CardHeader>
      <CardContent className={cn('flex flex-col gap-4', isLoading && 'pointer-events-none')}>
        <div className='space-y-3'>
          {showEditor ? (
            <SuccessionEstateAllocationEditor
              scopeTotal={estateTotal}
              currency={currency}
              beneficiaries={beneficiaries}
              percents={percents}
              onPercentsChange={onPercentsChange}
              segments={segments}
              selectedBeneficiaryId={selectedBeneficiaryId}
              onSelectBeneficiary={onSelectBeneficiary}
              onRemoveBeneficiary={onRemoveBeneficiary}
            />
          ) : segments && segments.length > 0 ? (
            <StackedAllocationBar
              segments={segments}
              selectedId={selectedBeneficiaryId}
              onSelect={(id) =>
                onSelectBeneficiary(
                  selectedBeneficiaryId === id || id === 'unallocated' ? null : id
                )
              }
              onRemove={isLoading ? undefined : onRemoveBeneficiary}
              removeLabel={removeLabel}
              ariaLabel={t('planning.succession.simpleAllocation')}
            />
          ) : (
            <div
              className='bg-fin-gray-800 h-3 w-full overflow-hidden rounded-full'
              role='img'
              aria-label={t('planning.succession.simpleAllocation')}
            />
          )}

          {showShimmerChips ? (
            <div className='flex flex-wrap gap-2'>
              {SHIMMER_PARTY_NAMES.map((name) => (
                <span
                  key={name}
                  className='border-border rounded-md border px-2 py-1 text-xs opacity-80'
                >
                  + <ShimmerText>{name}</ShimmerText>
                </span>
              ))}
            </div>
          ) : availableParties.length > 0 ? (
            <div className='flex flex-wrap gap-2'>
              {availableParties.map((party) => (
                <button
                  key={party.id}
                  type='button'
                  className='border-border hover:bg-muted/50 rounded-md border px-2 py-1 text-xs disabled:opacity-80'
                  disabled={isLoading}
                  onClick={() => onAddFromParty(party.id)}
                >
                  + {party.name}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        {drilldownLabel && drilldownContent ? (
          <div className='border-fin-gray-800 rounded-lg border p-3'>
            <p className='mb-2 text-sm font-medium'>{drilldownLabel}</p>
            {drilldownContent}
          </div>
        ) : null}

        <SuccessionModeToggle
          value={allocationMode}
          onChange={onAllocationModeChange}
          disabled={isLoading}
        />
      </CardContent>
    </Card>
  );
}
